import React, { useEffect, useState } from "react";
import { Menu, X, ShoppingBag, MessageCircle, Search } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "./ui/sheet";
import { NAV, CART } from "../constants/testIds";
import { useCart } from "../context/CartContext";

const links = [
    { id: NAV.linkProduits, label: "Produits", href: "#produits" },
    { id: NAV.linkExpertise, label: "Expertise", href: "#expertise" },
    { id: NAV.linkComparatif, label: "Comparatif", href: "#comparatif" },
    { id: NAV.linkProcessus, label: "Processus", href: "#processus" },
    { id: NAV.linkFaq, label: "FAQ", href: "#faq" },
];

const Navbar = ({ onQuoteClick }) => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { totalQty, setOpen: setCartOpen } = useCart();

    const focusCatalogSearch = (closeMenu) => {
        if (closeMenu) setOpen(false);
        setTimeout(() => {
            const el = document.getElementById("catalog-search");
            if (el) {
                const headerOffset = 120;
                const y = el.getBoundingClientRect().top + window.scrollY - headerOffset;
                window.scrollTo({ top: y, behavior: "smooth" });
                setTimeout(() => el.focus(), 400);
            }
        }, closeMenu ? 200 : 0);
    };

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid={NAV.root}
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "dp-glass border-b border-black/5"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-[1320px] mx-auto px-6 md:px-10 h-[68px] flex items-center justify-between">
                {/* Logo */}
                <a
                    href="#top"
                    data-testid={NAV.logo}
                    className="flex items-center gap-2.5 group"
                >
                    <svg width="28" height="28" viewBox="0 0 32 32" className="shrink-0">
                        <defs>
                            <linearGradient id="dpLogo" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor="#0a84ff" />
                                <stop offset="100%" stopColor="#00e5ff" />
                            </linearGradient>
                        </defs>
                        <rect x="2" y="2" width="28" height="28" rx="8" fill="#1C1C1E" />
                        <path d="M10 8 H17 a7 7 0 0 1 0 14 H10 Z" fill="url(#dpLogo)" />
                        <circle cx="22" cy="22" r="2.4" fill="#00e5ff" />
                    </svg>
                    <span className="font-display text-[17px] font-semibold tracking-tight">
                        Dentiprime
                    </span>
                </a>

                {/* Center links */}
                <nav className="hidden lg:flex items-center gap-1">
                    {links.map((l) => (
                        <a
                            key={l.id}
                            data-testid={l.id}
                            href={l.href}
                            className="px-4 py-2 text-[14px] text-neutral-700 hover:text-black rounded-full hover:bg-black/[0.04] transition-colors"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                {/* CTA */}
                <div className="hidden lg:flex items-center gap-2">
                    {/* Search button */}
                    <button
                        type="button"
                        onClick={focusCatalogSearch}
                        aria-label="Rechercher dans le catalogue"
                        className="relative w-10 h-10 rounded-full border border-black/10 hover:border-black/30 flex items-center justify-center transition-colors"
                    >
                        <Search className="w-4 h-4" />
                    </button>

                    {/* Cart button */}
                    <button
                        type="button"
                        onClick={() => setCartOpen(true)}
                        data-testid={CART.button}
                        aria-label="Voir le panier"
                        className="relative w-10 h-10 rounded-full border border-black/10 hover:border-black/30 flex items-center justify-center transition-colors"
                    >
                        <ShoppingBag className="w-4 h-4" />
                        {totalQty > 0 && (
                            <span
                                data-testid={CART.badge}
                                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#0a84ff] text-white text-[10px] font-mono-tech flex items-center justify-center tabular-nums leading-none"
                            >
                                {totalQty}
                            </span>
                        )}
                    </button>

                    {/* Devis CTA with WhatsApp icon */}
                    <button
                        type="button"
                        data-testid={NAV.ctaQuote}
                        onClick={onQuoteClick}
                        className="dp-btn dp-btn-primary text-[13px]"
                    >
                        <MessageCircle className="w-3.5 h-3.5 text-[#25D366]" />
                        Demander un devis
                    </button>
                </div>

                {/* Mobile actions */}
                <div className="lg:hidden flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => focusCatalogSearch(true)}
                        aria-label="Rechercher dans le catalogue"
                        className="relative w-10 h-10 rounded-full border border-black/10 flex items-center justify-center"
                    >
                        <Search className="w-4 h-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => setCartOpen(true)}
                        data-testid={`${CART.button}-mobile`}
                        aria-label="Voir le panier"
                        className="relative w-10 h-10 rounded-full border border-black/10 flex items-center justify-center"
                    >
                        <ShoppingBag className="w-4 h-4" />
                        {totalQty > 0 && (
                            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#0a84ff] text-white text-[10px] flex items-center justify-center tabular-nums leading-none">
                                {totalQty}
                            </span>
                        )}
                    </button>
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <button
                                type="button"
                                aria-label="Menu"
                                className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-black/10"
                            >
                                {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
                            </button>
                        </SheetTrigger>
                        <SheetContent
                            side="top"
                            className="lg:hidden top-[68px] h-auto bg-white/95 backdrop-blur-xl border-b border-black/5 [&>button]:hidden"
                        >
                            <nav className="flex flex-col gap-1">
                                {links.map((l) => (
                                    <SheetClose asChild key={l.id}>
                                        <a
                                            href={l.href}
                                            className="py-3 text-[15px] text-neutral-800"
                                        >
                                            {l.label}
                                        </a>
                                    </SheetClose>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => {
                                        setOpen(false);
                                        // Let the sheet close and Radix clean up scroll lock before opening the quote dialog.
                                        setTimeout(() => onQuoteClick(), 50);
                                    }}
                                    className="mt-3 dp-btn dp-btn-primary justify-center"
                                >
                                    <MessageCircle className="w-3.5 h-3.5 text-[#25D366]" />
                                    Demander un devis
                                </button>
                            </nav>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
};

export default Navbar;
